// ============================================
// HORIZONTAL MOON MAP GENERATOR
// Supports TWO moon canvases side by side
// Each moon has independent date, location, and phase
// ============================================

(function() {
    'use strict';

    // ============================================
    // DOM HELPERS
    // ============================================
    // Small plain-DOM helpers. Their semantics are deliberately those of the
    // library calls this file was written against, because the results are
    // observable: every helper is a silent no-op on a missing element, writes
    // apply to every match, and reads come from the first match. A bare
    // "#id" selector resolves to the single getElementById element.
    function hQ(sel, root) { return (root || document).querySelector(sel); }
    function hQA(sel, root) {
        if (!root && /^#[\w-]+$/.test(sel)) {
            var byId = document.getElementById(sel.slice(1));
            return byId ? [byId] : [];
        }
        return Array.prototype.slice.call((root || document).querySelectorAll(sel));
    }
    function hList(x) { return !x ? [] : (x.nodeType ? [x] : x); }

    // Field value read. undefined when there is no element; a select with
    // nothing selected reads null (not '').
    function hVal(el) {
        if (!el) return undefined;
        if (el.type === 'select-one') {
            var opt = el.options[el.selectedIndex];
            if (!opt || opt.disabled || (opt.parentNode.disabled && opt.parentNode.nodeName === 'OPTGROUP')) return null;
            return opt.value;
        }
        var v = el.value;
        return typeof v === 'string' ? v.replace(/\r/g, '') : (v == null ? '' : v);
    }

    // Field value write. null/undefined write ''; a select given a value it
    // has no option for ends up with selectedIndex -1.
    function hSetVal(els, v) {
        var s = (v == null) ? '' : String(v);
        hList(els).forEach(function(el) { el.value = s; });
    }

    // Text read concatenates every match; text write sets every match.
    function hText(els) {
        return hList(els).map(function(el) { return el.textContent; }).join('');
    }
    function hSetText(els, t) {
        hList(els).forEach(function(el) { el.textContent = t; });
    }

    // data-* read with the old .data() type conversion: "true"/"false"/
    // "null" become literals, numeric strings become numbers, JSON-looking
    // strings are parsed; a missing attribute reads undefined.
    function hData(el, key) {
        if (!el) return undefined;
        var v = el.getAttribute('data-' + key);
        if (v === null) return undefined;
        if (v === 'true') return true;
        if (v === 'false') return false;
        if (v === 'null') return null;
        if (v === +v + '') return +v;
        if (/^(?:\{[\w\W]*\}|\[[\w\W]*\])$/.test(v)) {
            try { return JSON.parse(v); } catch (e) { /* keep the string */ }
        }
        return v;
    }

    // Add/remove space-separated classes on every element, touching the
    // class attribute only when something actually changes.
    function hClass(els, add, names) {
        var list = names.split(' ').filter(Boolean);
        hList(els).forEach(function(el) {
            var todo = list.filter(function(n) { return el.classList.contains(n) !== add; });
            if (todo.length) el.classList[add ? 'add' : 'remove'].apply(el.classList, todo);
        });
    }

    // show()/hide(). hide() remembers the inline display it replaces;
    // show() puts that back, or clears the inline none, and if a stylesheet
    // still hides the element falls back to the tag's default display.
    var _hDisplayStore = new WeakMap();
    var _hDefaultDisplay = {};
    function hDefaultDisplay(el) {
        var name = el.nodeName;
        if (_hDefaultDisplay[name]) return _hDefaultDisplay[name];
        var temp = document.body.appendChild(document.createElement(name));
        var display = getComputedStyle(temp).display;
        temp.parentNode.removeChild(temp);
        if (display === 'none') display = 'block';
        _hDefaultDisplay[name] = display;
        return display;
    }
    function hHiddenWithinTree(el) {
        return el.style.display === 'none' ||
            (el.style.display === '' && el.isConnected && getComputedStyle(el).display === 'none');
    }
    function hShowHide(els, show) {
        var values = [];
        els.forEach(function(el, i) {
            if (!el.style) return;
            var display = el.style.display;
            if (show) {
                if (display === 'none') {
                    values[i] = _hDisplayStore.get(el) || null;
                    if (!values[i]) el.style.display = '';
                }
                if (el.style.display === '' && hHiddenWithinTree(el)) values[i] = hDefaultDisplay(el);
            } else if (display !== 'none') {
                values[i] = 'none';
                _hDisplayStore.set(el, display);
            }
        });
        els.forEach(function(el, i) { if (values[i] != null) el.style.display = values[i]; });
    }
    function hShow(els) { hShowHide(hList(els), true); }
    function hHide(els) { hShowHide(hList(els), false); }

    // Fades and slides. Each element has one animation queue, so a fade-out
    // requested mid fade-in waits its turn; an animation with nothing to do
    // (fading in a visible element) completes at once; hFxStop clears the
    // queue and jumps the running animation to its end state. End states:
    // display via hShowHide, animated inline styles restored to what they
    // were before the animation, overflow restored.
    var _hFxQueues = new WeakMap();
    function hFxQueue(el) {
        var q = _hFxQueues.get(el);
        if (!q) { q = { steps: [], current: null }; _hFxQueues.set(el, q); }
        return q;
    }
    function hFxEnqueue(el, step) {
        var q = hFxQueue(el);
        q.steps.push(step);
        if (!q.current) hFxDequeue(el);
    }
    function hFxDequeue(el) {
        var q = hFxQueue(el);
        q.current = null;
        var step = q.steps.shift();
        if (step) {
            q.current = { stop: null };
            step(q.current, function() { hFxDequeue(el); });
        }
    }
    function hFxStop(el) {
        var q = hFxQueue(el);
        q.steps = [];
        if (q.current && q.current.stop) q.current.stop();
    }
    // Tween inline styles ([name, from, to, unit]) with 'swing' easing.
    function hTween(slot, el, duration, props, finish) {
        var start = Date.now();
        var ended = false;
        function frame(p) {
            var eased = 0.5 - Math.cos(p * Math.PI) / 2;
            props.forEach(function(pr) { el.style[pr[0]] = (pr[1] + (pr[2] - pr[1]) * eased) + pr[3]; });
        }
        function end() {
            if (ended) return;
            ended = true;
            frame(1);
            finish();
        }
        function tick() {
            if (ended) return;
            var p = Math.min(1, (Date.now() - start) / duration);
            frame(p);
            if (p < 1) schedule(); else end();
        }
        function schedule() {
            if (document.hidden === false && window.requestAnimationFrame) window.requestAnimationFrame(tick);
            else setTimeout(tick, 13);
        }
        slot.stop = end;
        frame(0);
        schedule();
    }
    function hFade(els, show, duration) {
        hList(els).forEach(function(el) {
            hFxEnqueue(el, function(slot, done) {
                var hidden = hHiddenWithinTree(el);
                if (hidden !== show) { done(); return; }     // already in the target state
                var orig = el.style.opacity;
                var from, to;
                if (show) {
                    hShowHide([el], true);
                    from = 0;
                    to = parseFloat(getComputedStyle(el).opacity) || 0;
                } else {
                    from = parseFloat(getComputedStyle(el).opacity) || 0;
                    to = 0;
                }
                hTween(slot, el, duration, [['opacity', from, to, '']], function() {
                    if (!show) hShowHide([el], false);
                    el.style.opacity = orig;
                    done();
                });
            });
        });
    }
    var H_SLIDE_PROPS = ['marginTop', 'paddingTop', 'marginBottom', 'paddingBottom', 'height'];
    function hSlide(els, show, duration) {
        hList(els).forEach(function(el) {
            hFxEnqueue(el, function(slot, done) {
                var hidden = hHiddenWithinTree(el);
                if (hidden !== show) { done(); return; }     // already in the target state
                var style = el.style;
                var orig = {};
                H_SLIDE_PROPS.forEach(function(p) { orig[p] = style[p]; });
                var overflow = [style.overflow, style.overflowX, style.overflowY];
                // Display probe on a hidden element (show, then hide again):
                // leaves the same remembered display behind as the original
                // slide implementation did.
                if (getComputedStyle(el).display === 'none' && !_hDisplayStore.get(el)) {
                    hShowHide([el], true);
                    hShowHide([el], false);
                }
                style.overflow = 'hidden';
                if (hidden) hShowHide([el], true);
                var cs = getComputedStyle(el);
                var props = H_SLIDE_PROPS.map(function(p) {
                    var v = parseFloat(cs[p]) || 0;
                    return show ? [p, 0, v, 'px'] : [p, v, 0, 'px'];
                });
                hTween(slot, el, duration, props, function() {
                    style.overflow = overflow[0];
                    style.overflowX = overflow[1];
                    style.overflowY = overflow[2];
                    if (!hidden) hShowHide([el], false);
                    H_SLIDE_PROPS.forEach(function(p) { style[p] = orig[p]; });
                    done();
                });
            });
        });
    }

    // Delegated events. One native listener per (root, type); on each event
    // walk from the target up to the root and run every handler whose
    // selector matches the node — deepest node first, registration order
    // within a node — with `this` = the matched node. A handler that stops
    // propagation ends the walk (so a delegated handler on a deeper node can
    // shield one on its ancestor, even though both live on the same root);
    // returning false = preventDefault() + stopPropagation(). Clicks with a
    // non-primary button and clicks on disabled nodes are not delegated.
    var _hDelegations = [];
    function hDelegate(roots, type, selector, fn) {
        hList(roots).forEach(function(root) {
            var d = null;
            for (var i = 0; i < _hDelegations.length; i++) {
                if (_hDelegations[i].root === root && _hDelegations[i].type === type) { d = _hDelegations[i]; break; }
            }
            if (!d) {
                d = { root: root, type: type, handlers: [] };
                _hDelegations.push(d);
                root.addEventListener(type, hDelegateDispatcher(d));
            }
            d.handlers.push({ selector: selector, fn: fn });
        });
    }
    function hDelegateDispatcher(d) {
        return function(e) {
            if (e.type === 'click' && e.button >= 1) return;
            var matched = [];
            for (var cur = e.target; cur && cur !== d.root; cur = cur.parentNode || d.root) {
                if (cur.nodeType !== 1 || (e.type === 'click' && cur.disabled === true)) continue;
                var node = cur;
                var hs = d.handlers.filter(function(h) { return node.matches(h.selector); });
                if (hs.length) matched.push({ el: node, handlers: hs });
            }
            if (!matched.length) return;
            var stopped = false, immediate = false;
            e.stopPropagation = function() { stopped = true; Event.prototype.stopPropagation.call(e); };
            e.stopImmediatePropagation = function() { stopped = immediate = true; Event.prototype.stopImmediatePropagation.call(e); };
            try {
                for (var i = 0; i < matched.length && !stopped; i++) {
                    for (var j = 0; j < matched[i].handlers.length && !immediate; j++) {
                        if (matched[i].handlers[j].fn.call(matched[i].el, e) === false) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }
                }
            } finally {
                delete e.stopPropagation;
                delete e.stopImmediatePropagation;
            }
        };
    }

    // Direct listener on every element in the set (`this` = the element).
    function hOn(els, type, fn) {
        hList(els).forEach(function(el) { el.addEventListener(type, fn); });
    }

    // ============================================
    // STATE - Four independent moon states (2 active by default)
    // ============================================
    var moonInstances = {
        1: {
            scene: null,
            camera: null,
            renderer: null,
            moonMesh: null,
            moonGroup: null,
            lights: { ambient: null, directional: null, earthshine: null },
            currentDate: new Date(2023, 2, 14, 23, 11), // March 14, 2023
            currentLocation: { lat: 51.5074, lng: -0.1278, name: 'London, UK' },
            phaseInfo: { phase: 0, illumination: 0, phaseName: '' },
            initialized: false
        },
        2: {
            scene: null,
            camera: null,
            renderer: null,
            moonMesh: null,
            moonGroup: null,
            lights: { ambient: null, directional: null, earthshine: null },
            currentDate: new Date(2025, 5, 19, 23, 11), // June 19, 2025
            currentLocation: { lat: 53.4808, lng: -2.2426, name: 'Manchester, UK' },
            phaseInfo: { phase: 0, illumination: 0, phaseName: '' },
            initialized: false
        },
        3: {
            scene: null,
            camera: null,
            renderer: null,
            moonMesh: null,
            moonGroup: null,
            lights: { ambient: null, directional: null, earthshine: null },
            currentDate: new Date(2024, 0, 1, 23, 11), // Jan 1, 2024
            currentLocation: { lat: 40.7128, lng: -74.0060, name: 'New York, USA' },
            phaseInfo: { phase: 0, illumination: 0, phaseName: '' },
            initialized: false
        },
        4: {
            scene: null,
            camera: null,
            renderer: null,
            moonMesh: null,
            moonGroup: null,
            lights: { ambient: null, directional: null, earthshine: null },
            currentDate: new Date(2024, 0, 1, 23, 11), // Jan 1, 2024
            currentLocation: { lat: 48.8566, lng: 2.3522, name: 'Paris, France' },
            phaseInfo: { phase: 0, illumination: 0, phaseName: '' },
            initialized: false
        }
    };

    // ============================================
    // TEXTURE URLs
    // ============================================
    var MOON_TEXTURES = {
        silver: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_silver_v3-1k.jpg?v=1777886361',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_silver_v3-1k.jpg?v=1777886361'
        },
        bright_silver: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_bright_silver_v3-1k.jpg?v=1777886361',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_bright_silver_v3-1k.jpg?v=1777886361'
        },
        gold: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-new-cont_cur-GOLDEN3.png?v=1764999529',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-new-cont_cur-GOLDEN3.png?v=1764999529'
        },
        blue: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-BLUE.jpg',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-BLUE.jpg'
        },
        pink: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-PINK.jpg',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-moon-texture-PINK.jpg'
        }
    };

    var TEXTURES = {
        moon: MOON_TEXTURES.silver,
        displacement: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_displacement_v3-1k.jpg?v=1777886360',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_displacement_v3-1k.jpg?v=1777886360'
        },
        normal: {
            preview: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_normal_v3-1k.jpg?v=1777886360',
            render: 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-moon-tex_normal_v3-1k.jpg?v=1777886360'
        }
    };

    // ============================================
    // TEXTURE VARIANT SWITCHING
    // ============================================
    // Per-moon texture tracking: { 1: 'silver', 2: 'gold', 3: 'silver', 4: 'pink' }
    window.hMoonTextures = { 1: 'silver', 2: 'silver', 3: 'silver', 4: 'silver' };
    // Legacy compat
    window.currentHMoonTexture = 'silver';

    window.setHMoonTextureVariant = function(variant, moonId) {
        var textureConfig = MOON_TEXTURES[variant] || MOON_TEXTURES.silver;
        var textureUrl = textureConfig.preview;

        if (moonId) {
            // Per-moon texture switch
            window.hMoonTextures[moonId] = variant || 'silver';
            var loader = new THREE.TextureLoader();
            loader.crossOrigin = 'anonymous';
            loader.load(textureUrl, function(texture) {
                texture.anisotropy = 4;
                texture.minFilter = THREE.LinearMipmapLinearFilter;
                texture.magFilter = THREE.LinearFilter;
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                var inst = moonInstances[moonId];
                if (inst && inst.moonMesh) {
                    inst.moonMesh.material.map = texture;
                    inst.moonMesh.material.needsUpdate = true;
                    if (inst.renderer && inst.scene && inst.camera) {
                        inst.renderer.render(inst.scene, inst.camera);
                    }
                }
                console.log('🌙 H-Moon ' + moonId + ' texture switched to:', variant);
            });
        } else {
            // All moons at once (legacy)
            TEXTURES.moon = textureConfig;
            window.currentHMoonTexture = variant || 'silver';
            [1,2,3,4].forEach(function(id) { window.hMoonTextures[id] = variant || 'silver'; });
            var loader = new THREE.TextureLoader();
            loader.crossOrigin = 'anonymous';
            loader.load(textureUrl, function(texture) {
                texture.anisotropy = 4;
                texture.minFilter = THREE.LinearMipmapLinearFilter;
                texture.magFilter = THREE.LinearFilter;
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                Object.keys(moonInstances).forEach(function(key) {
                    var inst = moonInstances[key];
                    if (inst.moonMesh) {
                        inst.moonMesh.material.map = texture;
                        inst.moonMesh.material.needsUpdate = true;
                        if (inst.renderer && inst.scene && inst.camera) {
                            inst.renderer.render(inst.scene, inst.camera);
                        }
                    }
                });
                console.log('🌙 All H-Moon textures switched to:', variant);
            });
        }
    };

    // ============================================
    // BRIDGE EXPORTS
    // ============================================
    window.hMoonInstances = moonInstances;

    // ============================================
    // GOOGLE MAPS CALLBACK - Entry Point
    // ============================================
    // Split entry points (same pattern as moon-generator.js): the section
    // boots the scene on DOMContentLoaded via hMoonBootScene and lazy-loads
    // Google Maps on first interaction with callback=hMoonBindPlaces.
    // initHMoonMap keeps the original do-everything behaviour for compat.
    window.hMoonBindPlaces = function() {
        if (!(window.google && google.maps && google.maps.places)) return;
        // Setup Google Places Autocomplete for all moons
        setupAutocomplete(1);
        setupAutocomplete(2);
        setupAutocomplete(3);
        setupAutocomplete(4);
    };

    var _hSceneBooted = false;
    window.hMoonBootScene = function() {
        if (_hSceneBooted) return;
        _hSceneBooted = true;

        // Initialize date/time selectors for all moons. Isolated per call so a
        // failure in one (e.g. a UI helper running before a third-party
        // dependency is ready) can't take down the WebGL scene setup that
        // follows - that would silently kill every moon preview with no
        // error surfaced to the customer.
        try { initDateTimeSelectors(1); } catch (e) { console.warn('[hor-moon] initDateTimeSelectors(1) failed', e); }
        try { initDateTimeSelectors(2); } catch (e) { console.warn('[hor-moon] initDateTimeSelectors(2) failed', e); }
        try { initDateTimeSelectors(3); } catch (e) { console.warn('[hor-moon] initDateTimeSelectors(3) failed', e); }
        try { initDateTimeSelectors(4); } catch (e) { console.warn('[hor-moon] initDateTimeSelectors(4) failed', e); }

        // Initialize Three.js for default 2 moons
        try { initThreeJS(1); } catch (e) { console.warn('[hor-moon] initThreeJS(1) failed', e); }
        try { initThreeJS(2); } catch (e) { console.warn('[hor-moon] initThreeJS(2) failed', e); }

        // Load textures for default 2 moons
        try { loadMoonTextures(1); } catch (e) { console.warn('[hor-moon] loadMoonTextures(1) failed', e); }
        try { loadMoonTextures(2); } catch (e) { console.warn('[hor-moon] loadMoonTextures(2) failed', e); }

        // Setup UI handlers (including accordion)
        try { setupUI(); } catch (e) { console.warn('[hor-moon] setupUI failed', e); }

        // Setup accordion for moon sections
        try { setupAccordion(); } catch (e) { console.warn('[hor-moon] setupAccordion failed', e); }

        // Per-moon reorder arrows (swap two slots in place)
        try { setupReorderControls(); } catch (e) { console.warn('[hor-moon] setupReorderControls failed', e); }

        // Per-moon "Show time on print" toggle → re-render that moon's printed
        // date label (display-only; no moon re-render needed).
        hDelegate(document, 'change', '.h-show-time-cb', function() {
            var mid = parseInt(this.dataset.moon, 10);
            if (mid && moonInstances[mid] && moonInstances[mid].currentDate) updateDateDisplay(mid);
            // (autosave is wired separately via the .h-show-time-cb listener in
            // hor-moon-myjs.js, which reads the freshly-updated date fields.)
        });

        // Store initial template count — non-clobbering: order hydration can
        // run before this init on slow loads and may already have switched
        // to 3/4 moons. Resetting to 2 here makes the next save silently
        // drop moons 3/4 from the order.
        window.hMoonTemplateCount = window.hMoonTemplateCount || 2;

        // Handle window resize for responsive moon sizing
        var resizeTimeout;
        window.addEventListener('resize', function() {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(function() {
                var count = window.hMoonTemplateCount || 2;
                updateMoonItemSizes(count);
            }, 250);
        });

        // Initial sizing for proper display on page load
        updateMoonItemSizes(2);

        // Apply default font to all text elements on page load
        applyFont('Josefin Sans', 'both');

        // Mark generator as fully initialized
        window.hMoonGeneratorReady = true;

        console.log('🌙 Horizontal Moon Map initialized');
    };

    window.initHMoonMap = function() {
        console.log('🌙 Initializing Horizontal Moon Map (up to 4 moons)...');
        window.hMoonBindPlaces();
        window.hMoonBootScene();
    };

    // ============================================
    // ACCORDION FUNCTIONALITY
    // ============================================
    function setupAccordion() {
        // Toggle accordion on header click (supports both old and new card styles)
        hDelegate(document, 'click', '.h-moon-section-header, .h-moon-card-header', function() {
            var header = this;
            var moonId = hData(header, 'accordion-toggle');
            var contents = hQA('[data-accordion-content="' + moonId + '"]');

            // Toggle collapsed state
            header.classList.toggle('h-collapsed');
            contents.forEach(function(c) { c.classList.toggle('h-collapsed'); });
            updateCardSummary(moonId);

            console.log('📁 Moon ' + moonId + ' accordion toggled');
        });

        // Keep each collapsed header scannable: echo the moon's label next
        // to the card title so a long 3/4-moon list stays navigable.
        hDelegate(document, 'input', '[id^="h-moon"][id$="Name"]', function() {
            var m = this.id.match(/^h-moon(\d+)Name$/);
            if (m) updateCardSummary(m[1]);
        });
        for (var i = 1; i <= 4; i++) updateCardSummary(i);
    }

    function updateCardSummary(moonId) {
        var headers = hQA('.h-moon-card-header[data-accordion-toggle="' + moonId + '"]');
        if (!headers.length) return;
        var summaries = [];
        headers.forEach(function(h) { summaries = summaries.concat(hQA('.h-moon-card-summary', h)); });
        if (!summaries.length) {
            // Insert after every title (a copy per extra title); with no
            // title the new span stays detached.
            var titles = [];
            headers.forEach(function(h) { titles = titles.concat(hQA('.h-moon-card-title', h)); });
            var span = document.createElement('span');
            span.className = 'h-moon-card-summary';
            summaries = titles.length ? titles.map(function(t, i) {
                var node = (i === titles.length - 1) ? span : span.cloneNode(true);
                t.parentNode.insertBefore(node, t.nextSibling);
                return node;
            }) : [span];
        }
        var label = (hVal(hQ('#h-moon' + moonId + 'Name')) || '').trim();
        hSetText(summaries, label ? '· ' + label : '');
    }

    // Push a moon's canonical location (the hidden #h-address_form1_moonN
    // mirror) into what the customer actually reads: the
    // <gmp-place-autocomplete> box. That element renders its input in a CLOSED
    // shadow root, so `value` is the only handle there is.
    //
    // Written as a real value rather than a placeholder: the design HAS this
    // location, and grey ghost text reads as an empty field.
    //
    // Callers: setupAutocomplete on bind, and order/draft hydration once it has
    // written the mirror. Returns false when the box doesn't exist yet (Maps
    // not loaded) — harmless, the bind-time call covers that case.
    window.hMoonSyncLocationInput = function(moonId) {
        var mirror = document.getElementById('h-address_form1_moon' + moonId);
        var pac = document.getElementById('h-moon-location-input-' + moonId);
        if (!mirror || !pac) return false;
        var label = (mirror.value || '').trim();
        try { pac.value = label; } catch (e) { /* noop */ }
        var ph = label || 'Search for a location...';
        try { pac.placeholder = ph; } catch (e) { /* noop */ }
        try { pac.setAttribute && pac.setAttribute('placeholder', ph); } catch (e) { /* noop */ }
        return true;
    };

    // ============================================
    // SETUP AUTOCOMPLETE FOR A MOON
    // ============================================
    // Migrated from the legacy google.maps.places.Autocomplete widget to the
    // new PlaceAutocompleteElement web component (mirrors starmap-v2 + single
    // moon). The new element renders its OWN input, so #h-address_form1_moonN
    // is now a HIDDEN canonical mirror; the element mounts where it used to sit
    // and, on gmp-select, writes the resolved "Name, Country" into the mirror +
    // the cart-payload/detail fields and fires `change` so existing listeners
    // (button text, autosave) keep working. NO body-fixed scroll-lock — the
    // customizer scrolls on the window, so iOS handles the focus scroll
    // natively (see [[feedback_moon_pac_body_lock_removal]]).
    function setupAutocomplete(moonId) {
        var input = document.getElementById('h-address_form1_moon' + moonId);
        if (!input) return;
        if (!(window.google && google.maps && google.maps.places && google.maps.places.PlaceAutocompleteElement)) return;
        if (input.__hPacBound) return;          // guard against double-bind
        input.__hPacBound = true;

        // UK-biased ranking (neither field RESTRICTS — international results just
        // rank lower), same config as the other Skylit customizers.
        // SkylitPlaces swaps in our look-alike (assets/skylit-place-autocomplete.js),
        // which debounces typing and bills picks at the Essentials Places tier.
        var pacOptions = {
            requestedRegion: 'gb',
            locationBias: { north: 60.9, south: 49.5, east: 1.8, west: -8.7 }
        };
        var pac = window.SkylitPlaces
          ? window.SkylitPlaces.createAutocomplete(pacOptions)
          : new google.maps.places.PlaceAutocompleteElement(pacOptions);
        pac.id = 'h-moon-location-input-' + moonId;
        pac.className = 'h-pac-element';
        input.parentNode.insertBefore(pac, input);
        // Show whatever the hidden mirror already holds — a returning
        // customer's saved location. This is one half of a race: Maps loads
        // lazily on first touch, so binding can happen either side of
        // hydration. hMoonSyncLocationInput is called from BOTH sides;
        // whichever runs last leaves the box correct.
        window.hMoonSyncLocationInput(moonId);

        pac.addEventListener('gmp-select', async function(event) {
            try {
                var place = event.placePrediction.toPlace();
                await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'] });
                var lat = place.location.lat();
                var lng = place.location.lng();
                // Label = tapped name + its country (e.g. "Manchester, UK").
                var name = place.displayName || '';
                var country = '';
                var comps = place.addressComponents || [];
                for (var ci = 0; ci < comps.length; ci++) {
                    var ctypes = comps[ci].types || [];
                    if (ctypes.indexOf('country') !== -1) {
                        var sc = comps[ci].shortText;
                        if (sc === 'GB') country = 'UK';
                        else if (sc === 'US') country = 'USA';
                        else country = comps[ci].longText || sc || '';
                        break;
                    }
                }
                var picked = name
                    ? ((country && name.indexOf(country) === -1) ? (name + ', ' + country) : name)
                    : (place.formattedAddress || '');

                var instance = moonInstances[moonId];
                instance.currentLocation.lat = lat;
                instance.currentLocation.lng = lng;
                instance.currentLocation.name = picked;

                input.value = picked;
                hSetVal(hQ('#h-custom_address_moon' + moonId), picked);
                hSetVal(hQ('#h-custom_location_moon' + moonId), picked);   // detail-edit field
                input.dispatchEvent(new Event('change', { bubbles: true }));
                hSetText(hQ('#h-moon-location-' + moonId), picked);        // canvas display

                console.log('📍 Moon ' + moonId + ' location changed:', picked);
                reloadMoonWithAnimation(moonId);
            } catch (e) { console.warn('[hmoon] place select failed for moon ' + moonId, e); }
        });
    }

    // ============================================
    // THREE.JS INITIALIZATION FOR A MOON
    // ============================================
    function initThreeJS(moonId) {
        var canvas = document.getElementById('h-moon-canvas-' + moonId);
        var container = document.getElementById('h-moon-canvas-container-' + moonId);
        if (!canvas || !container) {
            console.error('❌ Canvas or container not found for moon ' + moonId);
            return;
        }

        var instance = moonInstances[moonId];
        // This only sizes the canvas, and it must not be able to throw
        // before the renderer (and its WebGL recovery attach below) is set up.
        var screenWidth = document.documentElement.clientWidth;  // viewport width excluding the scrollbar

        // Get size based on screen width (default 2-moon layout)
        var size;
        if (screenWidth <= 480) {
            size = 80;
        } else if (screenWidth <= 768) {
            size = 100;
        } else if (screenWidth <= 834) {
            size = 170;
        } else if (screenWidth <= 1194) {
            size = 150;
        } else {
            size = 210;
        }

        // Scene
        instance.scene = new THREE.Scene();
        instance.scene.background = null; // Transparent background

        // Camera
        instance.camera = new THREE.PerspectiveCamera(0.521, 1, 0.1, 1000);
        instance.camera.position.z = 225;

        // Moon group
        instance.moonGroup = new THREE.Object3D();
        instance.scene.add(instance.moonGroup);

        // Renderer
        instance.renderer = new THREE.WebGLRenderer({
            canvas: canvas,
            antialias: true,
            preserveDrawingBuffer: true,
            alpha: true
        });
        instance.renderer.setSize(size, size);
        instance.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        instance.renderer.outputColorSpace = THREE.SRGBColorSpace;
        instance.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        instance.renderer.toneMappingExposure = 0.80;

        // WebGL recovery. Without this a lost context leaves this moon's
        // preview silently blank, which on mobile happens under ordinary
        // memory pressure. See snippets/webgl-recovery.liquid.
        if (window.SkylitWebGL && instance.renderer && instance.renderer.domElement) {
            window.SkylitWebGL.attach(instance.renderer.domElement, {
                label: 'family moon preview',
                critical: true,
                onRestore: function () {
                    try {
                        instance.renderer.resetState && instance.renderer.resetState();
                        updateMoon(moonId);
                        render(moonId);
                        return true;
                    } catch (e) {
                        return false;
                    }
                },
                onFail: function () {
                    var host = document.getElementById('h-moon-canvas-container-' + moonId) || instance.renderer.domElement.parentNode || document.body;
                    window.SkylitWebGL.showFallback(host, { label: 'family moon preview' });
                }
            });
        }

        // Lights
        instance.lights.ambient = new THREE.AmbientLight(0x404050, 0.25);
        instance.moonGroup.add(instance.lights.ambient);

        instance.lights.directional = new THREE.DirectionalLight(0xffffff, 3.0);
        instance.lights.directional.position.set(100, 0, 100);
        instance.moonGroup.add(instance.lights.directional);

        instance.lights.earthshine = new THREE.DirectionalLight(0x777788, 1.08);
        instance.lights.earthshine.position.set(-100, 0, -100);
        instance.moonGroup.add(instance.lights.earthshine);

        console.log('🎬 Three.js initialized for moon ' + moonId);
    }

    // ============================================
    // TEXTURE LOADING FOR A MOON
    // ============================================
    function loadMoonTextures(moonId) {
        showLoader();

        var loader = new THREE.TextureLoader();
        var moonUrl = TEXTURES.moon.preview;
        var displacementUrl = TEXTURES.displacement.preview;
        var normalUrl = TEXTURES.normal.preview;

        Promise.all([
            loadTexture(loader, moonUrl, 'Moon ' + moonId),
            loadTexture(loader, displacementUrl, 'Displacement ' + moonId),
            loadTexture(loader, normalUrl, 'Normal ' + moonId)
        ])
        .then(function(textures) {
            createMoon(moonId, textures[0], textures[1], textures[2]);
            hideLoader();
            hHidePosterIfDone(moonId);
        })
        .catch(function(error) {
            console.error('⚠️ Texture loading error for moon ' + moonId + ':', error);
            loader.load(moonUrl, function(texture) {
                createMoon(moonId, texture, null, null);
                hideLoader();
                hHidePosterIfDone(moonId);
            });
        });
    }

    // Fade out the static LCP poster (hor-moon-skylit.liquid #h-moon-lcp-poster)
    // once the second default moon has a real render.
    function hHidePosterIfDone(moonId) {
        if (moonId < 2) return;
        var p = document.getElementById('h-moon-lcp-poster');
        if (p) { p.style.opacity = '0'; setTimeout(function(){ p.remove(); }, 600); }
    }

    function loadTexture(loader, url, name) {
        return new Promise(function(resolve, reject) {
            loader.load(
                url,
                function(texture) {
                    texture.anisotropy = 4;
                    texture.minFilter = THREE.LinearMipmapLinearFilter;
                    texture.magFilter = THREE.LinearFilter;
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                    resolve(texture);
                },
                undefined,
                function(error) {
                    console.warn('⚠️ Failed to load:', name);
                    resolve(null);
                }
            );
        });
    }

    // ============================================
    // MOON CREATION
    // ============================================
    function createMoon(moonId, moonTexture, displacementTexture, normalTexture) {
        var instance = moonInstances[moonId];
        var geometry = new THREE.SphereGeometry(1, 256, 256);

        var materialConfig = {
            color: 0xf5f5f5,
            map: moonTexture,
            roughness: 0.95,
            metalness: 0.0,
            envMapIntensity: 0.1
        };

        if (displacementTexture) {
            materialConfig.displacementMap = displacementTexture;
            materialConfig.displacementScale = 0.028;
        }

        if (normalTexture) {
            materialConfig.normalMap = normalTexture;
            materialConfig.normalScale = new THREE.Vector2(1.6, 1.6);
        }

        var material = new THREE.MeshStandardMaterial(materialConfig);
        instance.moonMesh = new THREE.Mesh(geometry, material);
        instance.moonGroup.add(instance.moonMesh);
        instance.moonMesh.rotation.y = -THREE.MathUtils.degToRad(90);

        updateMoon(moonId);
        render(moonId);
    }

    // ============================================
    // MOON PHASE UPDATE
    // ============================================
    function updateMoon(moonId) {
        var instance = moonInstances[moonId];
        if (!instance.moonMesh) return;

        var moonIllum = SunCalc.getMoonIllumination(instance.currentDate);
        var phaseName = getPhaseName(moonIllum.phase);
        var illuminationPercent = Math.round(moonIllum.fraction * 100);

        instance.phaseInfo = {
            phase: moonIllum.phase,
            illumination: illuminationPercent,
            phaseName: phaseName
        };

        console.log('🌙 Moon ' + moonId + ' Phase:', phaseName, '| Illumination:', illuminationPercent + '%');

        applyMoonTransforms(moonId, moonIllum, phaseName, illuminationPercent);
    }

    function applyMoonTransforms(moonId, moonIllum, phaseName, illuminationPercent) {
        var instance = moonInstances[moonId];
        var positionAngle = computeAxisPositionAngle(instance.currentDate);
        var brightLimb = computeBrightLimbAngle(instance.currentDate);

        // Libration
        var libration = { elon: 0, elat: 0 };
        if (typeof Astronomy !== 'undefined' && Astronomy.Libration) {
            try {
                libration = Astronomy.Libration(instance.currentDate);
            } catch(e) {
                console.warn('⚠️ Libration calculation failed for moon ' + moonId);
            }
        }

        // Parallactic angle
        var parallacticAngle = 0;
        if (typeof Astronomy !== 'undefined') {
            try {
                parallacticAngle = calculateParallacticAngle(instance.currentDate, instance.currentLocation.lat, instance.currentLocation.lng);
            } catch(e) {
                parallacticAngle = 0;
            }
        }

        // Reset rotations
        instance.moonMesh.rotation.x = 0;
        instance.moonMesh.rotation.y = -THREE.MathUtils.degToRad(90);
        instance.moonMesh.rotation.z = 0;
        instance.moonGroup.rotation.set(0, 0, 0);

        // Apply transforms
        var xAxis = new THREE.Vector3(1, 0, 0);
        var yAxis = new THREE.Vector3(0, 1, 0);
        var zAxis = new THREE.Vector3(0, 0, 1);

        var lonLibration = THREE.MathUtils.degToRad(libration.elon);
        var latLibration = THREE.MathUtils.degToRad(libration.elat);

        instance.moonMesh.rotateOnWorldAxis(yAxis, -lonLibration);
        instance.moonMesh.rotateOnWorldAxis(xAxis, latLibration);
        instance.moonMesh.rotateOnWorldAxis(zAxis, positionAngle);
        instance.moonGroup.rotation.z = -parallacticAngle;

        // Update lighting
        updateLightingForPhase(moonId, moonIllum, brightLimb);

        // Update UI
        updatePhaseDisplay(moonId, phaseName, illuminationPercent);

        render(moonId);
    }

    // ============================================
    // LIGHTING UPDATE
    // ============================================

    // ============================================
    // MOON HALO (family) — see .h-moon-halo in hor-moon.css
    // ============================================
    // Per moon, because each has its own date and so its own Sun direction.
    // The angle is read off the directional light's WORLD position after the
    // scene graph updates rather than re-derived from bright-limb / position /
    // parallactic angles.
    //
    // SIZING. The baked art seats the moon's limb at 0.2505 of its own box, so
    //     halo width = disc radius in CSS px / 0.2505
    // Two things this must not do:
    //  1. Assume the disc is a fixed fraction of the canvas. The family canvas
    //     is clipped by border-radius:50% (not the single moon's
    //     clip-path: circle(49.5%)), and measures 0.4992 — close, but the
    //     fraction is read from the pixels so it cannot drift.
    //  2. Measure once. .h-moon-canvas is 16cqw and every layout (2/3/4
    //     moons), composition and orientation changes it. Sizing only at
    //     render time left a stale value: measured 2.089x the canvas where the
    //     formula wanted 1.976, putting the ring off the limb with a visible
    //     gap — badly so on small portrait moons. A ResizeObserver per canvas
    //     re-sizes on every layout change.
    function updateHalo(moonId, instance, fraction) {
        try {
            var container = document.getElementById('h-moon-canvas-container-' + moonId);
            var canvas = document.getElementById('h-moon-canvas-' + moonId);
            if (!container || !canvas) return;

            // Size is pure CSS now (199.3% of .h-moon-halo-wrap), so there
            // is nothing to measure or keep in sync here.
            var f = Math.max(0, Math.min(1, fraction || 0));
            container.style.setProperty('--h-glow-strength',
                (0.30 + 0.70 * Math.pow(f, 0.6)).toFixed(3));

            if (instance && instance.lights && instance.lights.directional && instance.moonGroup) {
                instance.moonGroup.updateMatrixWorld(true);
                var pos = new THREE.Vector3();
                instance.lights.directional.getWorldPosition(pos);
                if (pos.x || pos.y) {
                    var deg = Math.atan2(pos.x, pos.y) * 180 / Math.PI;
                    container.style.setProperty('--h-glow-angle', deg.toFixed(1) + 'deg');
                }
            }
        } catch (e) { /* decoration must never break a moon */ }
    }

    function updateLightingForPhase(moonId, moonIllum, brightLimb) {
        var instance = moonInstances[moonId];
        var fraction = moonIllum.fraction;

        var F = Math.PI / 2 - brightLimb;
        var C = Math.acos(1 - 2 * fraction);

        var lightX = -Math.sin(C) * Math.cos(F);
        var lightY = Math.sin(C) * Math.sin(F);
        var lightZ = -Math.cos(C);

        instance.lights.directional.position.set(lightX * 100, lightY * 100, lightZ * 100);
        instance.lights.earthshine.position.set(-lightX * 100, -lightY * 100, -lightZ * 100);

        if (fraction < 0.25) {
            instance.lights.ambient.intensity = 0.35;
            instance.lights.directional.intensity = 2.6;
        } else if (fraction < 0.5) {
            instance.lights.ambient.intensity = 0.3;
            instance.lights.directional.intensity = 2.8;
        } else {
            instance.lights.ambient.intensity = 0.25;
            instance.lights.directional.intensity = 2.4;
        }

        updateHalo(moonId, instance, fraction);
    }

    // ============================================
    // ASTRONOMY CALCULATIONS
    // ============================================
    function deg2rad(d) {
        return d * Math.PI / 180;
    }

    function computeBrightLimbAngle(date) {
        var observer = new Astronomy.Observer(0, 0, 0);
        var moonEqu = Astronomy.Equator('Moon', date, observer, true, true);
        var sunEqu = Astronomy.Equator('Sun', date, observer, true, true);

        var alpha = deg2rad(moonEqu.ra * 15);
        var delta = deg2rad(moonEqu.dec);
        var alpha0 = deg2rad(sunEqu.ra * 15);
        var delta0 = deg2rad(sunEqu.dec);

        var num = Math.cos(delta0) * Math.sin(alpha0 - alpha);
        var den = Math.sin(delta0) * Math.cos(delta) - Math.cos(delta0) * Math.sin(delta) * Math.cos(alpha0 - alpha);

        return Math.atan2(num, den);
    }

    function computeAxisPositionAngle(date) {
        var RA_P = deg2rad(269.9949);
        var DEC_P = deg2rad(66.5392);

        var observer = new Astronomy.Observer(0, 0, 0);
        var moonEqu = Astronomy.Equator('Moon', date, observer, true, true);

        var alpha = deg2rad(moonEqu.ra * 15);
        var delta = deg2rad(moonEqu.dec);

        var num = Math.cos(DEC_P) * Math.sin(RA_P - alpha);
        var den = Math.sin(DEC_P) * Math.cos(delta) - Math.cos(DEC_P) * Math.sin(delta) * Math.cos(RA_P - alpha);

        return Math.atan2(num, den);
    }

    function calculateParallacticAngle(date, lat, lng) {
        var observer = new Astronomy.Observer(lat, lng, 0);
        var equ = Astronomy.Equator('Moon', date, observer, true, true);
        var gast = Astronomy.SiderealTime(date);
        var hourAngle = (gast + lng / 15 - equ.ra) * 15;
        hourAngle = THREE.MathUtils.degToRad(hourAngle);

        var latRad = THREE.MathUtils.degToRad(lat);
        var decRad = THREE.MathUtils.degToRad(equ.dec);

        var sinQ = Math.sin(hourAngle) * Math.cos(latRad);
        var cosQ = Math.sin(latRad) * Math.cos(decRad) - Math.cos(latRad) * Math.sin(decRad) * Math.cos(hourAngle);

        return Math.atan2(sinQ, cosQ);
    }

    // ============================================
    // PHASE NAME HELPER
    // ============================================
    function getPhaseName(phase) {
        if (phase < 0.025 || phase > 0.975) return 'New Moon';
        if (phase < 0.225) return 'Waxing Crescent';
        if (phase < 0.275) return 'First Quarter';
        if (phase < 0.475) return 'Waxing Gibbous';
        if (phase < 0.525) return 'Full Moon';
        if (phase < 0.725) return 'Waning Gibbous';
        if (phase < 0.775) return 'Last Quarter';
        return 'Waning Crescent';
    }

    // ============================================
    // RENDER
    // ============================================

    // Per-moon halo hue: swap a pre-tinted asset for that moon's texture.
    // Runtime tinting needed a masked child inside the blended wrapper, which
    // renders as a dark disc on Safari 18 despite correct computed styles.
    function updateHaloTint(moonId) {
        try {
            var halo = document.querySelector('#h-moon-canvas-container-' + moonId + ' .h-moon-halo');
            var map = window.SKYLIT_MOON_HALOS;
            if (!halo || !map) return;
            var variant = (window.hMoonTextures && window.hMoonTextures[moonId]) || 'silver';
            var url = map[variant] || map.silver;
            if (!url) return;
            var next = 'url("' + url + '")';
            if (halo.style.backgroundImage !== next) halo.style.backgroundImage = next;
        } catch (e) { /* decoration must never break a moon */ }
    }

    var _tintTimers = {};
    function scheduleHaloTint(moonId) {
        if (_tintTimers[moonId]) clearTimeout(_tintTimers[moonId]);
        _tintTimers[moonId] = setTimeout(function () { updateHaloTint(moonId); }, 90);
    }

    function render(moonId) {
        var instance = moonInstances[moonId];
        if (instance.renderer && instance.scene && instance.camera) {
            instance.renderer.render(instance.scene, instance.camera);
            scheduleHaloTint(moonId);
        }
    }

    // ============================================
    // DATE/TIME SELECTORS FOR A MOON
    // ============================================
    function initDateTimeSelectors(moonId) {
        var instance = moonInstances[moonId];
        var defaultDate = instance.currentDate;

        // Populate Days
        var daysHtml = '';
        for (var d = 1; d <= 31; d++) {
            var selected = (d === defaultDate.getDate()) ? 'selected' : '';
            daysHtml += '<option value="' + d + '" ' + selected + '>' + d + '</option>';
        }
        hQA('.h-bear-dates[data-moon="' + moonId + '"]').forEach(function(el) { el.innerHTML = daysHtml; });

        // Populate Months
        var monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
        var monthsHtml = '';
        for (var m = 0; m < 12; m++) {
            var selected = (m === defaultDate.getMonth()) ? 'selected' : '';
            monthsHtml += '<option value="' + m + '" ' + selected + '>' + monthNames[m] + '</option>';
        }
        hQA('.h-bear-months[data-moon="' + moonId + '"]').forEach(function(el) { el.innerHTML = monthsHtml; });

        // Populate Years
        var yearsHtml = '';
        for (var y = 1920; y <= 2035; y++) {
            var selected = (y === defaultDate.getFullYear()) ? 'selected' : '';
            yearsHtml += '<option value="' + y + '" ' + selected + '>' + y + '</option>';
        }
        hQA('.h-bear-years[data-moon="' + moonId + '"]').forEach(function(el) { el.innerHTML = yearsHtml; });

        // Populate Hours
        var hoursHtml = '';
        for (var h = 0; h <= 23; h++) {
            var displayHour = h.toString().padStart(2, '0');
            var selected = (h === defaultDate.getHours()) ? 'selected' : '';
            hoursHtml += '<option value="' + h + '" ' + selected + '>' + displayHour + '</option>';
        }
        hQA('.h-moon-hours[data-moon="' + moonId + '"]').forEach(function(el) { el.innerHTML = hoursHtml; });

        // Populate Minutes
        var minutesHtml = '';
        for (var min = 0; min <= 59; min++) {
            var displayMin = min.toString().padStart(2, '0');
            var selected = (min === defaultDate.getMinutes()) ? 'selected' : '';
            minutesHtml += '<option value="' + min + '" ' + selected + '>' + displayMin + '</option>';
        }
        hQA('.h-moon-minutes[data-moon="' + moonId + '"]').forEach(function(el) { el.innerHTML = minutesHtml; });

        // Change handlers
        hOn(hQA('.h-bear-dates[data-moon="' + moonId + '"], .h-bear-months[data-moon="' + moonId + '"], .h-bear-years[data-moon="' + moonId + '"], .h-moon-hours[data-moon="' + moonId + '"], .h-moon-minutes[data-moon="' + moonId + '"]'), 'change', function() {
            var mid = hData(this, 'moon');
            updateDateFromSelectors(mid);
            reloadMoonWithAnimation(mid);
        });

        // Initial update
        updateDateFromSelectors(moonId);
        updateDateDisplay(moonId);
    }

    function updateDateFromSelectors(moonId) {
        var day = parseInt(hVal(hQ('.h-bear-dates[data-moon="' + moonId + '"]'))) || 1;
        var month = parseInt(hVal(hQ('.h-bear-months[data-moon="' + moonId + '"]'))) || 0;
        var year = parseInt(hVal(hQ('.h-bear-years[data-moon="' + moonId + '"]'))) || new Date().getFullYear();
        var hoursVal = parseInt(hVal(hQ('.h-moon-hours[data-moon="' + moonId + '"]')));
        var hours = isNaN(hoursVal) ? 23 : hoursVal;
        var minutesVal = parseInt(hVal(hQ('.h-moon-minutes[data-moon="' + moonId + '"]')));
        var minutes = isNaN(minutesVal) ? 11 : minutesVal;

        moonInstances[moonId].currentDate = new Date(year, month, day, hours, minutes);
        console.log('📅 Moon ' + moonId + ' date updated:', moonInstances[moonId].currentDate.toLocaleString());

        updateDateDisplay(moonId);
    }

    // ============================================
    // DISPLAY UPDATES
    // ============================================
    function updatePhaseDisplay(moonId, phaseName, illuminationPercent) {
        // Phase display removed per user request
        // Keeping function for backward compatibility but not updating UI
    }

    var H_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                         'July', 'August', 'September', 'October', 'November', 'December'];

    // Global date display format: 'written' | 'numeric' | 'plain' | 'us'.
    // The preview labels AND the submitted/printed date both come from this —
    // they were previously out of sync (preview always written, print always
    // numeric). Hydration overrides it from the saved order's date shape.
    window.hMoonDateFormat = window.hMoonDateFormat || 'written';

    window.formatHMoonDate = function(day, monthIdx, year, format) {
        var f = format || window.hMoonDateFormat || 'written';
        var pad = function(n) { return String(n).padStart(2, '0'); };
        switch (f) {
            case 'numeric': return pad(day) + '/' + pad(monthIdx + 1) + '/' + year;
            case 'plain':   return day + ' ' + H_MONTH_NAMES[monthIdx] + ' ' + year;
            case 'us':      return H_MONTH_NAMES[monthIdx] + ' ' + day + getOrdinalSuffix(day) + ', ' + year;
            // No comma after the month, matching the star map print (2026-11-01).
            default:        return day + getOrdinalSuffix(day) + ' ' + H_MONTH_NAMES[monthIdx] + ' ' + year;
        }
    };

    // Re-render every moon's date label in the current format (called by the
    // step-2 "Date format" cycle button).
    window.hMoonReformatAllDates = function() {
        for (var i = 1; i <= 4; i++) {
            if (moonInstances[i] && moonInstances[i].currentDate) updateDateDisplay(i);
        }
    };

    function updateDateDisplay(moonId) {
        var instance = moonInstances[moonId];
        var date = instance.currentDate;

        var day = date.getDate();
        var month = date.getMonth();
        var year = date.getFullYear();

        var dateStr = window.formatHMoonDate(day, month, year);

        // Per-moon "Show time on print": append this moon's own time (12-hour)
        // when its checkbox is ticked — e.g. a birthday moon. Display-only: the
        // moon-phase math already ran off currentDate, and the renderer prints
        // this date string verbatim (render_hor_moon.py).
        var showTimeCb = document.getElementById('h-moon-time-on-' + moonId);
        if (showTimeCb && showTimeCb.checked) {
            var h = date.getHours(), mn = date.getMinutes();
            var mer = h >= 12 ? 'PM' : 'AM';
            var h12 = h % 12; if (h12 === 0) h12 = 12;
            dateStr += ' - ' + h12 + ':' + String(mn).padStart(2, '0') + ' ' + mer;
        }

        // Always update the hidden field for form submission
        hSetVal(hQ('#h-auto_date_moon' + moonId), dateStr);

        // Sync detail edit input with the new date value
        hSetVal(hQ('#h-custom_date_moon' + moonId), dateStr);

        // Update the canvas display
        hSetText(hQ('#h-moon-date-' + moonId), dateStr);
    }

    // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
    function getOrdinalSuffix(day) {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    }

    // ============================================
    // LOADING ANIMATION (CSS-only loader - no external scripts)
    // ============================================
    function showLoader() {
        hFade(hQA('.myloader'), true, 200);
    }

    function hideLoader() {
        hFade(hQA('.myloader'), false, 300);
    }

    function reloadMoonWithAnimation(moonId) {
        showLoader();
        setTimeout(function() {
            updateMoon(moonId);
            hideLoader();
        }, 100);
    }

    // ============================================
    // UI SETUP
    // ============================================
    function setupUI() {
        // Text input handlers - respect empty values (show empty space on preview)
        hOn(hQ('#h-textTitle'), 'input', function() {
            var title = hVal(this);
            hSetText(hQ('#h-skyMap-title'), title);
        });

        hOn(hQ('#h-textSubtitle'), 'input', function() {
            var subtitle = hVal(this);
            hSetText(hQ('#h-skyMap-subtitle'), subtitle);
        });

        hOn(hQ('#h-moon1Name'), 'input', function() {
            var name = hVal(this);
            hSetText(hQ('#h-moon-name-1'), name);
        });

        hOn(hQ('#h-moon2Name'), 'input', function() {
            var name = hVal(this);
            hSetText(hQ('#h-moon-name-2'), name);
        });

        // Step navigation (tab clicks + Next Step) lives ONLY in
        // hor-moon-myjs.js setupNavigation()/navigateToStep(). The tab and
        // .h-next__step click handlers that used to sit here were a second,
        // parallel binding on the same elements — each click ran BOTH
        // systems, so "Next Step" advanced two steps at a time (1 -> 3,
        // skipping Moment). Do not re-add navigation handlers in this file.

        // Finish tiles (type / frame / size / gift) and design tiles
        // (template / composition / background / moon hue) are bound to the
        // store (HMoonCore in hor-moon-myjs.js, hor-moon-ui.js).

        // Font scope tabs and font tiles are bound to the store too
        // (HMoonCore.setFontScope / chooseFont -> applyFont below).

        // Moon 3 label input handler - respect empty values
        hOn(hQ('#h-moon3Name'), 'input', function() {
            var name = hVal(this);
            hSetText(hQ('#h-moon-name-3'), name);
        });

        // Moon 4 label input handler - respect empty values
        hOn(hQ('#h-moon4Name'), 'input', function() {
            var name = hVal(this);
            hSetText(hQ('#h-moon-name-4'), name);
        });

        // Initialize UI state
        var settings = hQA('.h-element__settings');
        if (settings.length) {
            hClass(settings[0], true, 'h-active__settings h-visited');
            hShow(hQA('.h-content__settings', settings[0]));
        }
        var laterContents = [];
        settings.slice(1).forEach(function(el) {
            hQA('.h-content__settings', el).forEach(function(c) {
                if (laterContents.indexOf(c) === -1) laterContents.push(c);
            });
        });
        hHide(laterContents);

        // ============================================
        // Optional Detail Edits Handlers
        // ============================================
        setupDetailEdits();

        console.log('🎨 H-Moon UI initialized');
    }

    // ============================================
    // OPTIONAL DETAIL EDITS HANDLERS
    // ============================================
    function setupDetailEdits() {
        // Accordion toggle is now handled by moon section accordion handler
        // (uses same .h-moon-section-header pattern with data-accordion-toggle)

        // Custom Date Text input handlers (for all 4 moons) - respect empty values
        for (var i = 1; i <= 4; i++) {
            (function(moonId) {
                hOn(hQ('#h-custom_date_moon' + moonId), 'input', function() {
                    var customDate = hVal(this);
                    // Update canvas display with whatever the user types (empty = blank)
                    hSetText(hQ('#h-moon-date-' + moonId), customDate);
                });

                // Custom Location Text input handlers - respect empty values
                hOn(hQ('#h-custom_location_moon' + moonId), 'input', function() {
                    var customLocation = hVal(this);
                    // Update canvas display with whatever the user types (empty = blank)
                    hSetText(hQ('#h-moon-location-' + moonId), customLocation);
                });
            })(i);
        }

        // Initial sync of detail edit fields with current values
        syncDetailEditsWithCurrentValues();

        console.log('✏️ Detail Edits handlers initialized');
    }

    // Sync detail edit inputs with current location/date values
    function syncDetailEditsWithCurrentValues() {
        for (var i = 1; i <= 4; i++) {
            syncDetailEditForMoon(i);
        }
    }

    // Sync a single moon's detail edit fields
    function syncDetailEditForMoon(moonId) {
        // Sync date - set as actual value
        var dateText = hText(hQ('#h-moon-date-' + moonId));
        if (dateText) {
            hSetVal(hQ('#h-custom_date_moon' + moonId), dateText);
            hSetVal(hQ('#h-auto_date_moon' + moonId), dateText);
        }

        // Sync location - set as actual value
        var locationText = hText(hQ('#h-moon-location-' + moonId));
        if (locationText) {
            hSetVal(hQ('#h-custom_location_moon' + moonId), locationText);
        }
    }

    // Export for use in other functions
    window.syncDetailEditForMoon = syncDetailEditForMoon;

    // ============================================
    // PRODUCT OPTIONS (Step 4 - Dynamic Liquid)
    // ============================================
    // ============================================
    // TEMPLATE LAYOUT (2/3/4 moons)
    // ============================================
    function updateTemplateLayout(count) {
        console.log('🌙 Template changed to ' + count + ' moons');

        // Update CSS class on container for layout
        var container = hQ('#h-moons-container');
        hClass(container, false, 'h-layout-2 h-layout-3 h-layout-4');
        hClass(container, true, 'h-layout-' + count);

        // Show/hide moon 3 in sidebar, preview, and detail edits
        if (count >= 3) {
            hQA('.h-moon-3-section').forEach(hFxStop);
            hSlide(hQA('.h-moon-3-section'), true, 180);
            hShow(hQA('.h-moon-3-label'));
            hShow(hQA('.h-moon-3-preview'));

            // Initialize moon 3 if not already done
            if (!moonInstances[3].initialized) {
                initThreeJS(3);
                loadMoonTextures(3);
                moonInstances[3].initialized = true;
            }
        } else {
            hQA('.h-moon-3-section').forEach(hFxStop);
            hSlide(hQA('.h-moon-3-section'), false, 140);
            hHide(hQA('.h-moon-3-label'));
            hHide(hQA('.h-moon-3-preview'));
        }

        // Show/hide moon 4 in sidebar, preview, and detail edits
        if (count >= 4) {
            hQA('.h-moon-4-section').forEach(hFxStop);
            hSlide(hQA('.h-moon-4-section'), true, 180);
            hShow(hQA('.h-moon-4-label'));
            hShow(hQA('.h-moon-4-preview'));

            // Initialize moon 4 if not already done
            if (!moonInstances[4].initialized) {
                initThreeJS(4);
                loadMoonTextures(4);
                moonInstances[4].initialized = true;
            }
        } else {
            hQA('.h-moon-4-section').forEach(hFxStop);
            hSlide(hQA('.h-moon-4-section'), false, 140);
            hHide(hQA('.h-moon-4-label'));
            hHide(hQA('.h-moon-4-preview'));
        }

        // Store template count for form submission
        window.hMoonTemplateCount = count;

        // Update moon item sizes based on layout
        updateMoonItemSizes(count);

        // The last visible moon has nothing to its right — re-evaluate which
        // reorder arrows apply now the print is a different length.
        try { refreshReorderArrows(); } catch (e) { /* decoration */ }
    }

    // ============================================
    // FLIP LAYOUT CHOREOGRAPHY
    // Measure every preview element, apply the layout change, then
    // animate each one from its old box to its new one: surviving moons
    // glide and resize, a removed moon eases out in place, a new moon
    // eases in after the others settle. (Replaces the old "re-pop
    // everything" animation, which made unchanged moons flicker.)
    // ============================================
    var FLIP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
    var flipReady = false;
    window.addEventListener('load', function() {
        // Hydration re-clicks the saved options right after load — let
        // those land without animation, then enable the choreography.
        setTimeout(function() { flipReady = true; }, 900);
    });

    function flipLayout(mutate) {
        var reduce = window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!flipReady || reduce || !document.body.animate || flipLayout._active) {
            mutate();
            return;
        }
        flipLayout._active = true;

        var moons = Array.prototype.slice.call(
            document.querySelectorAll('#h-moons-container .h-moon-item'));
        var bands = Array.prototype.slice.call(
            document.querySelectorAll('.h-title-section, .h-subtitle-section'));
        var visible = function(el) {
            return el.offsetParent !== null && getComputedStyle(el).display !== 'none';
        };
        var first = new Map();
        moons.concat(bands).forEach(function(el) {
            if (visible(el)) first.set(el, el.getBoundingClientRect());
        });

        try {
            mutate();
        } finally {
            flipLayout._active = false;
        }

        var entering = 0;
        moons.forEach(function(el) {
            if (el.getAnimations) el.getAnimations().forEach(function(a) { a.cancel(); });
            var f = first.get(el);
            var vis = visible(el);
            if (f && vis) {
                // Survivor: glide + resize from the old box to the new one.
                var l = el.getBoundingClientRect();
                var dx = (f.left + f.width / 2) - (l.left + l.width / 2);
                var dy = (f.top + f.height / 2) - (l.top + l.height / 2);
                var ds = l.width ? f.width / l.width : 1;
                if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(ds - 1) < 0.005) return;
                el.animate([
                    { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + ds + ')' },
                    { transform: 'none' }
                ], { duration: 520, easing: FLIP_EASE });
            } else if (!f && vis) {
                // Newcomer: ease in once the survivors are on their way.
                el.animate([
                    { opacity: 0, transform: 'scale(0.6)' },
                    { opacity: 1, transform: 'none' }
                ], { duration: 460, delay: 140 + 70 * entering++, easing: FLIP_EASE, fill: 'backwards' });
            } else if (f && !vis) {
                // Removed: lift it out of the flow at its old spot and ease
                // it away while the survivors move into the freed space.
                var prev = el.getAttribute('style') || '';
                el.style.cssText = prev + ';display:block;position:fixed;left:' + f.left +
                    'px;top:' + f.top + 'px;width:' + f.width + 'px;height:' + f.height +
                    'px;margin:0;max-width:none;flex:none;z-index:3;pointer-events:none;';
                var exit = el.animate([
                    { opacity: 1, transform: 'none' },
                    { opacity: 0, transform: 'scale(0.6)' }
                ], { duration: 340, easing: 'cubic-bezier(0.4, 0, 0.7, 0.2)' });
                exit.onfinish = exit.oncancel = function() { el.setAttribute('style', prev); };
            }
        });
        // Title/subtitle bands: translate only — scaling would distort text.
        bands.forEach(function(el) {
            var f = first.get(el);
            if (!f || !visible(el)) return;
            if (el.getAnimations) el.getAnimations().forEach(function(a) { a.cancel(); });
            var l = el.getBoundingClientRect();
            var dx = f.left - l.left;
            var dy = f.top - l.top;
            if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
            el.animate([
                { transform: 'translate(' + dx + 'px,' + dy + 'px)' },
                { transform: 'none' }
            ], { duration: 520, easing: FLIP_EASE });
        });
    }
    window.hMoonFlip = flipLayout;

    // Expose so the composition picker can re-run sizing when switching to/from quad-portrait
    window.hMoonUpdateItemSizes = function(){
        updateMoonItemSizes(window.hMoonTemplateCount || 2);
    };

    // ============================================
    // UPDATE MOON ITEM SIZES FOR LAYOUT
    // ============================================
    function updateMoonItemSizes(count) {
        var items = hQA('#h-moons-container .h-moon-item');
        function sizeItems(maxWidth, flex) {
            items.forEach(function(el) {
                el.style.maxWidth = maxWidth;
                el.style.flex = flex;
            });
        }

        // Reset all items first
        sizeItems('', '');

        // Get canvas size based on layout and screen width
        var screenWidth = document.documentElement.clientWidth;  // viewport width excluding the scrollbar
        var canvasSize = 210; // Default for 2 moons on desktop

        if (count === 2) {
            sizeItems('45%', '1');
            // Size based on screen width
            if (screenWidth <= 480) {
                canvasSize = 80;
            } else if (screenWidth <= 768) {
                canvasSize = 90;
            } else if (screenWidth <= 834) {
                canvasSize = 150;
            } else if (screenWidth <= 1194) {
                canvasSize = 150;
            } else {
                canvasSize = 190;
            }
        } else if (count === 3) {
            sizeItems('30%', '1');
            if (screenWidth <= 480) {
                canvasSize = 65;
            } else if (screenWidth <= 768) {
                canvasSize = 75;
            } else if (screenWidth <= 834) {
                canvasSize = 170;
            } else if (screenWidth <= 1194) {
                canvasSize = 190;
            } else {
                canvasSize = 140;
            }
        } else if (count === 4) {
            var compFrame = hQ('.h-custom-frame');
            var isQuadPortrait = !!compFrame && compFrame.getAttribute('data-composition') === 'quad-portrait';
            if (isQuadPortrait) {
                sizeItems('50%', '0 0 50%');
                if (screenWidth <= 480) {
                    canvasSize = 110;
                } else if (screenWidth <= 768) {
                    canvasSize = 140;
                } else if (screenWidth <= 1194) {
                    canvasSize = 220;
                } else {
                    canvasSize = 220;
                }
            } else {
                sizeItems('22%', '1');
                if (screenWidth <= 480) {
                    canvasSize = 50;
                } else if (screenWidth <= 768) {
                    canvasSize = 60;
                } else if (screenWidth <= 834) {
                    canvasSize = 130;
                } else if (screenWidth <= 1194) {
                    canvasSize = 130;
                } else {
                    canvasSize = 110;
                }
            }
        }

        // Resize all active moon renderers to maintain 1:1 ratio
        for (var i = 1; i <= count; i++) {
            resizeMoonRenderer(i, canvasSize);
        }
    }

    // ============================================
    // RESIZE MOON RENDERER
    // ============================================
    function resizeMoonRenderer(moonId, size) {
        var instance = moonInstances[moonId];
        if (instance && instance.renderer) {
            // The canvas's DISPLAYED size comes from container queries and
            // tracks the preview frame, which can be far larger than the
            // legacy breakpoint table value passed in as `size` — leaving
            // the WebGL buffer at the table value meant CSS upscaled it
            // and the moons looked soft. Render the buffer at the real
            // on-screen size instead (capped at 512 CSS px: with the 2x
            // pixel-ratio cap that's a 1024px buffer, matching the 1k
            // textures). Moons render on demand, not per-frame, so the
            // bigger buffer costs nothing at runtime and the fast-loading
            // 1k textures are untouched.
            var displayed = instance.renderer.domElement.getBoundingClientRect().width;
            var target = Math.round(Math.max(size, Math.min(displayed || size, 512)));
            instance.renderer.setSize(target, target);
            render(moonId);
        }
    }

    // ============================================
    // BACKGROUND SWITCHER
    // ============================================
    var BACKGROUNDS = {
        'family3': 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-personalised-moon-phases-family-background3.webp?v=1765254076',
        'family1': 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-galaxy-background-render-cleaner.webp?v=1765303138',
        'family2': 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-skylit-studio-starry-night-sky-shooting-star-wallpaper.webp?v=1765303702',
        'black': 'https://cdn.shopify.com/s/files/1/0710/5278/7987/files/skylit-studio-skylit-studio-black-wallpaper.webp?v=1765303906'
    };

    // Solid colours — same palette/hex values as the moon-skylit customizer
    // swatches. fg flips the frame text dark on the light backgrounds.
    // The admin renderer mirrors this map (SOLID_BG in render_hor_moon.py).
    var SOLID_BACKGROUNDS = {
        'white':   { bg: '#fcfcfc', fg: '#000000' },
        'navy':    { bg: '#0a1628', fg: '#ffffff' },
        'cream':   { bg: '#f5e6d1', fg: '#1a1020' },
        'deepred': { bg: '#a01830', fg: '#ffffff' }
    };

    // Preload the full-size backgrounds once the page is idle so the
    // first selection cross-fades instead of fading to a still-loading
    // image. (The picker tiles use separate thumbnail URLs.)
    window.addEventListener('load', function() {
        var kick = window.requestIdleCallback || function(cb) { setTimeout(cb, 1500); };
        kick(function() {
            Object.keys(BACKGROUNDS).forEach(function(k) {
                var img = new Image();
                img.src = BACKGROUNDS[k];
            });
        });
    });

    function applyBackground(bgKey) {
        var solid = SOLID_BACKGROUNDS[bgKey];
        var frames = hQA('.h-custom-frame');
        var frame = frames[0];
        var bgUrl = BACKGROUNDS[bgKey] || BACKGROUNDS['family3'];
        var target = solid
            ? { backgroundImage: 'none', backgroundColor: solid.bg, color: solid.fg }
            : { backgroundImage: 'url(' + bgUrl + ')', backgroundColor: '#000', color: '#fff' };
        function paint() {
            frames.forEach(function(el) {
                el.style.backgroundImage = target.backgroundImage;
                el.style.backgroundColor = target.backgroundColor;
                el.style.color = target.color;
            });
        }

        var reduceMotion = window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (frame && !reduceMotion) {
            // Cross-fade: background-image isn't animatable, so paint the
            // incoming background on the ::before overlay, fade it in,
            // then commit it to the frame (with the frame's own
            // transition suspended so the reveal doesn't double-fade).
            frame.style.setProperty('--h-bg-next',
                solid ? solid.bg : 'url(' + bgUrl + ')');
            frames.forEach(function(el) { el.style.color = target.color; });
            hClass(frames, true, 'h-bg-fade');
            clearTimeout(applyBackground._commit);
            applyBackground._commit = setTimeout(function() {
                frame.style.transition = 'none';
                paint();
                hClass(frames, false, 'h-bg-fade');
                frame.style.removeProperty('--h-bg-next');
                void frame.offsetWidth;
                frame.style.transition = '';
            }, 380);
        } else {
            paint();
        }
        console.log('🖼️ Background changed to:', bgKey);

        // Store for form submission
        window.hMoonBackground = bgKey;
    }

    // ============================================
    // FONT SWITCHER
    // ============================================
    // Catalogue mirrors moon-skylit's customizer-font-picker snippet so
    // the two customisers offer the same fonts. Keep legacy entries
    // (Righteous, Fredericka the Great) for saved orders that may still
    // reference them, even though they're no longer in the picker.
    var FONT_MAP = {
        // Regular
        'Josefin Sans':        "'Josefin Sans', sans-serif",
        'Prata':               "'Prata', serif",
        'Spectral':            "'Spectral', serif",
        'Poppins':             "'Poppins', sans-serif",
        'Playfair Display':    "'Playfair Display', serif",
        'IM Fell English SC':  "'IM Fell English SC', serif",
        // Handwritten
        'Dancing Script':      "'Dancing Script', cursive",
        'Caveat':              "'Caveat', cursive",
        'Kaushan Script':      "'Kaushan Script', cursive",
        'Great Vibes':         "'Great Vibes', cursive",
        'Sacramento':          "'Sacramento', cursive",
        'Parisienne':          "'Parisienne', cursive",
        'Alex Brush':          "'Alex Brush', cursive",
        'Allura':              "'Allura', cursive",
        'Yellowtail':          "'Yellowtail', cursive",
        'Satisfy':             "'Satisfy', cursive",
        'Pacifico':            "'Pacifico', cursive",
        'Aguafina Script':     "'Aguafina Script', cursive",
        // Legacy — preserved for backward compat with saved orders
        'Righteous':           "'Righteous', cursive",
        'Fredericka the Great':"'Fredericka the Great', cursive"
    };

    // Two scopes mirror moon-skylit's message/details split. For hor-moon:
    //   message → frame-level title + subtitle (poetic header text)
    //   details → per-moon name + date + location (the per-moon labels)
    //   both    → apply to both
    // window.hMoonFont stays as the "primary" font (= message font) for
    // backward-compatibility with the existing save schema.
    // The chosen fonts and scope live in the store (hor-moon-myjs.js);
    // window.hMoonFontScope / hMoonMessageFont / hMoonDetailsFont / hMoonFont
    // are accessors onto it.

    function applyFont(fontName, scope) {
        scope = scope || 'both';
        var fontFamily = FONT_MAP[fontName] || FONT_MAP['Josefin Sans'];

        if (scope === 'message' || scope === 'both') {
            hList(hQ('#h-skyMap-title')).forEach(function(el) { el.style.fontFamily = fontFamily; });
            hList(hQ('#h-skyMap-subtitle')).forEach(function(el) { el.style.fontFamily = fontFamily; });
            window.hMoonMessageFont = fontName;
        }
        if (scope === 'details' || scope === 'both') {
            hQA('.h-moon-name-text').forEach(function(el) { el.style.fontFamily = fontFamily; });
            hQA('.h-moon-date-text').forEach(function(el) { el.style.fontFamily = fontFamily; });
            hQA('.h-moon-location-text').forEach(function(el) { el.style.fontFamily = fontFamily; });
            window.hMoonDetailsFont = fontName;
        }

        console.log('🔤 Font changed (scope=' + scope + '):', fontName);
    }

    // frameType null = a frameless product (Print / Canvas).
    function applyFrameStyle(frameType) {
        var frames = hQA('.h-custom-frame');
        hClass(frames, false, 'frame-none frame-black frame-white frame-natural frame-dark-brown');

        if (!frameType) {
            hClass(frames, true, 'frame-none');
        } else if (frameType === 'Black') {
            hClass(frames, true, 'frame-black');
        } else if (frameType === 'White') {
            hClass(frames, true, 'frame-white');
        } else if (frameType === 'Natural') {
            hClass(frames, true, 'frame-natural');
        } else if (frameType === 'Dark Brown') {
            hClass(frames, true, 'frame-dark-brown');
        }

        // Frame colour swaps don't change border-width, but Print→Framed
        // (frame-none → frame-X) does (0 → 22px), and that flips the
        // .h-frame-container's expected size. Refresh the container-query
        // engine so cqh-positioned children stay aligned.
        if (typeof window.hMoonRefreshFrameContainer === 'function') {
            window.hMoonRefreshFrameContainer();
        }
    }

    window.hApplyFrameStyle = applyFrameStyle;
    window.hApplyBackground = applyBackground;
    window.hApplyFont = applyFont;

    // ============================================
    // EXPORT FUNCTIONS
    // ============================================
    window.exportHMoonCanvas = function(moonId, width, height) {
        var instance = moonInstances[moonId];
        if (!instance || !instance.renderer) {
            console.warn('⚠️ Moon ' + moonId + ' renderer not available for export');
            return '';
        }

        try {
            // Detect mobile and adjust export size to prevent memory issues
            var isMobile = window.innerWidth <= 768;
            var exportWidth = width || (isMobile ? 256 : 512);
            var exportHeight = height || (isMobile ? 256 : 512);

            // Store original settings
            var originalSize = instance.renderer.getSize(new THREE.Vector2());
            var originalPixelRatio = instance.renderer.getPixelRatio();

            // Reset pixel ratio to 1 for consistent export size
            // This prevents mobile high-DPI screens from creating oversized buffers
            instance.renderer.setPixelRatio(1);
            instance.renderer.setSize(exportWidth, exportHeight);
            render(moonId);

            // Capture the canvas data
            var dataURL = '';
            try {
                dataURL = instance.renderer.domElement.toDataURL('image/jpeg', 0.85);
            } catch (canvasError) {
                console.warn('⚠️ Canvas toDataURL failed for moon ' + moonId + ':', canvasError.message);
                // Try with lower quality as fallback
                try {
                    dataURL = instance.renderer.domElement.toDataURL('image/jpeg', 0.5);
                } catch (fallbackError) {
                    console.warn('⚠️ Fallback canvas export also failed for moon ' + moonId);
                    dataURL = '';
                }
            }

            // Restore original settings
            instance.renderer.setPixelRatio(originalPixelRatio);
            instance.renderer.setSize(originalSize.x, originalSize.y);
            render(moonId);

            return dataURL;
        } catch (error) {
            console.error('❌ Export failed for moon ' + moonId + ':', error.message);
            return '';
        }
    };

    window.updateHMoon = function(moonId) {
        reloadMoonWithAnimation(moonId);
    };

    // Update moon from selector values and reload - used by scroll pickers
    window.updateHMoonFromSelectors = function(moonId) {
        updateDateFromSelectors(moonId);
        reloadMoonWithAnimation(moonId);
    };

    window.getHMoonData = function(moonId) {
        var instance = moonInstances[moonId];
        return {
            date: instance.currentDate,
            location: instance.currentLocation,
            phaseInfo: instance.phaseInfo
        };
    };

    // Override a moon's currentDate with an explicit Date (e.g. a UTC instant
    // reconstructed from a stored iso_timestamp) and re-render. Used on order
    // reload so rotation matches the renderer regardless of the reloading
    // browser's timezone.
    window.setHMoonDate = function(moonId, date) {
        if (!moonInstances[moonId] || !date || isNaN(date.getTime())) return;
        moonInstances[moonId].currentDate = date;
        reloadMoonWithAnimation(moonId);
    };


    // ============================================
    // MOON REORDER — swap two slots in place
    // ============================================
    // The slot index IS the printed position: the renderer templates
    // (renderer/templates/hor_moon/hor_moon_*.html) hard-slot {{moon1_*}} ..
    // {{moon4_*}} left to right, and D1 stores flat moonN_ columns. So
    // "move this moon left" exchanges the CONTENTS of two slots and leaves the
    // indices, canvases, payload keys and renderer alone — nothing downstream
    // has to learn that a reorder happened.

    /* @lm-test-export:start hMoonArrowState */
    function hMoonArrowState(index, count) {
        var i = parseInt(index, 10);
        var n = parseInt(count, 10);
        if (!i || !n || i < 1 || i > n) return { prev: false, next: false };
        return { prev: i > 1, next: i < n };
    }
    /* @lm-test-export:end hMoonArrowState */

    // Everything that identifies a moon rather than its position. Anything
    // missing from these lists stays behind when a moon moves, so keep them in
    // step with the per-moon markup in sections/hor-moon-skylit.liquid.
    var HM_SLOT_FIELDS = [
        '#h-moon{n}Name',
        '#h-address_form1_moon{n}',
        '#h-custom_address_moon{n}',
        '#h-custom_location_moon{n}',
        '#h-custom_date_moon{n}',
        '#h-auto_date_moon{n}',
        '.h-bear-dates[data-moon="{n}"]',
        '.h-bear-months[data-moon="{n}"]',
        '.h-bear-years[data-moon="{n}"]',
        '.h-moon-hours[data-moon="{n}"]',
        '.h-moon-minutes[data-moon="{n}"]'
    ];
    var HM_SLOT_TEXT = ['#h-moon-name-{n}', '#h-moon-date-{n}', '#h-moon-location-{n}'];

    function hmSlotSel(tpl, id) { return tpl.replace('{n}', id); }

    function readMoonSlot(id) {
        var inst = moonInstances[id] || {};
        var snap = { values: {}, text: {} };
        HM_SLOT_FIELDS.forEach(function (tpl) { snap.values[tpl] = hVal(hQ(hmSlotSel(tpl, id))); });
        HM_SLOT_TEXT.forEach(function (tpl) { snap.text[tpl] = hText(hQA(hmSlotSel(tpl, id))); });
        var showTimeCb = hQ('#h-moon-time-on-' + id);
        snap.showTime = showTimeCb ? showTimeCb.checked : undefined;
        // The date and time the customer actually sees are picker TRIGGER
        // buttons that initScrollPickers() injects over the (hidden) selects;
        // their text is only rewritten by the picker's own confirm. Move the
        // rendered text rather than reformatting it, so it always matches
        // whatever the picker last wrote.
        snap.dateTrigger = hText(hQA('.h-date-picker-trigger[data-moon="' + id + '"] .h-date-picker-trigger-text'));
        snap.timeTrigger = hText(hQA('.h-time-picker-trigger[data-moon="' + id + '"] .h-time-picker-trigger-text'));
        // <gmp-place-autocomplete> renders its input in a CLOSED shadow root —
        // there is no input to reach from the light DOM. The visible search
        // text is the element's own `value` property.
        var pacEl = document.getElementById('h-moon-location-input-' + id);
        snap.pacValue = pacEl ? pacEl.value : null;
        snap.texture = (window.hMoonTextures && window.hMoonTextures[id]) || 'silver';
        // The Date OBJECT moves, never a rebuild from the day/month/year
        // selects: updateDateFromSelectors constructs it in the browser's local
        // timezone, which is exactly the drift the iso_timestamp pass on order
        // reload exists to undo. Same for the rendered date/location strings —
        // copied verbatim so a hand-typed custom date survives the move.
        snap.currentDate = inst.currentDate;
        snap.currentLocation = inst.currentLocation;
        snap.phaseInfo = inst.phaseInfo;
        snap.enteredLocation = !!(window.hUserEnteredLocations || {})['moon' + id];
        snap.selectedDate = !!(window.hUserSelectedDates || {})['moon' + id];
        return snap;
    }

    function writeMoonSlot(id, snap) {
        Object.keys(snap.values).forEach(function (tpl) {
            var v = snap.values[tpl];
            hSetVal(hQA(hmSlotSel(tpl, id)), v === undefined ? '' : v);
        });
        Object.keys(snap.text).forEach(function (tpl) {
            hSetText(hQA(hmSlotSel(tpl, id)), snap.text[tpl]);
        });
        hQA('#h-moon-time-on-' + id).forEach(function (cb) { cb.checked = !!snap.showTime; });
        if (snap.dateTrigger !== undefined) {
            hSetText(hQA('.h-date-picker-trigger[data-moon="' + id + '"] .h-date-picker-trigger-text'), snap.dateTrigger);
        }
        if (snap.timeTrigger !== undefined) {
            hSetText(hQA('.h-time-picker-trigger[data-moon="' + id + '"] .h-time-picker-trigger-text'), snap.timeTrigger);
        }

        if (moonInstances[id]) {
            moonInstances[id].currentDate = snap.currentDate;
            moonInstances[id].currentLocation = snap.currentLocation;
            moonInstances[id].phaseInfo = snap.phaseInfo;
        }

        if (window.hMoonTextures) window.hMoonTextures[id] = snap.texture;

        if (window.hUserEnteredLocations) window.hUserEnteredLocations['moon' + id] = snap.enteredLocation;
        if (window.hUserSelectedDates) window.hUserSelectedDates['moon' + id] = snap.selectedDate;

        // The Places element's own search text has to move too, or the slot
        // keeps showing the address the customer typed for the moon that just
        // left it. Setting `value` is the only handle — its input lives in a
        // closed shadow root.
        var pac = document.getElementById('h-moon-location-input-' + id);
        if (pac) {
            var label = (snap.pacValue !== null && snap.pacValue !== undefined)
                ? snap.pacValue
                : (snap.values['#h-address_form1_moon{n}'] || '').trim();
            try { pac.value = label; } catch (e) { /* noop */ }
            // Before Maps loads there is nothing to type into, so a returning
            // customer sees the location as the placeholder instead — the same
            // convention setupAutocomplete uses when it seeds a saved order.
            var ph = label || 'Search for a location...';
            try { pac.placeholder = ph; } catch (e) { /* noop */ }
            try { pac.setAttribute && pac.setAttribute('placeholder', ph); } catch (e) { /* noop */ }
        }
    }

    // The preview items never move — their CONTENT does. So invert the usual
    // FLIP: after the swap, show each item at the box its new content came
    // from, then glide it home. Reads as the two moons passing each other.
    function hmSwapFlip(a, b, mutate) {
        var elA = document.querySelector('#h-moons-container .h-moon-item[data-moon="' + a + '"]');
        var elB = document.querySelector('#h-moons-container .h-moon-item[data-moon="' + b + '"]');
        var reduce = window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!elA || !elB || reduce || !document.body.animate) { mutate(); return; }

        var ra = elA.getBoundingClientRect();
        var rb = elB.getBoundingClientRect();
        mutate();
        var dx = rb.left - ra.left;
        var dy = rb.top - ra.top;
        [[elA, dx, dy], [elB, -dx, -dy]].forEach(function (pair) {
            var el = pair[0];
            if (el.getAnimations) el.getAnimations().forEach(function (an) { an.cancel(); });
            el.animate([
                { transform: 'translate(' + pair[1] + 'px,' + pair[2] + 'px)' },
                { transform: 'none' }
            ], { duration: 520, easing: FLIP_EASE });
        });
    }

    var hmSwapBusy = false;

    window.hMoonSwapMoons = function (a, b) {
        a = parseInt(a, 10);
        b = parseInt(b, 10);
        var count = window.hMoonTemplateCount || 2;
        if (!a || !b || a === b) return false;
        if (a < 1 || b < 1 || a > count || b > count) return false;
        if (hmSwapBusy) return false;
        hmSwapBusy = true;

        try {
            var snapA = readMoonSlot(a);
            var snapB = readMoonSlot(b);
            var texChanged = snapA.texture !== snapB.texture;

            hmSwapFlip(a, b, function () {
                writeMoonSlot(a, snapB);
                writeMoonSlot(b, snapA);
                if (texChanged && typeof window.setHMoonTextureVariant === 'function') {
                    window.setHMoonTextureVariant(snapB.texture, a);
                    window.setHMoonTextureVariant(snapA.texture, b);
                }
                // updateMoon rather than reloadMoonWithAnimation: the canvases
                // must repaint synchronously, before the glide starts, and one
                // swap shouldn't flash the full-screen loader twice.
                try { updateMoon(a); } catch (e) { console.warn('[hor-moon] swap re-render ' + a + ' failed', e); }
                try { updateMoon(b); } catch (e) { console.warn('[hor-moon] swap re-render ' + b + ' failed', e); }
                updateCardSummary(a);
                updateCardSummary(b);
                updateHaloTint(a);
                updateHaloTint(b);
            });
        } finally {
            hmSwapBusy = false;
        }

        if (typeof window.updateHAddToCartButtonText === 'function') {
            window.updateHAddToCartButtonText();
        }
        if (typeof window.hMoonScheduleAutosave === 'function') {
            window.hMoonScheduleAutosave();
        }
        console.log('🌙 Swapped moon slots ' + a + ' and ' + b);
        return true;
    };

    function refreshReorderArrows() {
        var count = window.hMoonTemplateCount || 2;
        hQA('.h-moon-reorder').forEach(function (el) {
            var id = parseInt(el.getAttribute('data-reorder-moon'), 10);
            var state = hMoonArrowState(id, count);
            // visibility, not display: the header must not reflow when an end
            // moon loses an arrow.
            hClass(hQA('.h-reorder-prev', el), !state.prev, 'h-reorder-off');
            hClass(hQA('.h-reorder-next', el), !state.next, 'h-reorder-off');
        });
    }
    window.hMoonRefreshReorderArrows = refreshReorderArrows;

    // Reorder mode: pin the preview arrows open so a moon can be moved by
    // tapping it on the print itself. Desktop reveals them on hover; a touch
    // screen has no hover, so the Shuffle button in the floating toolbar is
    // how a phone gets the same direct control.
    // Anything that isn't an arrow or the toolbar itself means "done
    // reordering" — the tools flyout has usually collapsed by then (tapping a
    // moon closes it), so without this the pinned arrows would have no
    // reachable off switch. The arrows are excluded so a swap doesn't end the
    // mode, and the toolbar is excluded so the Shuffle button's own click
    // still does the toggling.
    function hmReorderOutside(e) {
        if (e.target.closest && e.target.closest('.h-reorder-btn, #hmoonToolbar, #hmoonToolbarFab')) return;
        window.hMoonSetReorderMode(false);
    }
    function hmReorderKey(e) {
        if (e.key === 'Escape') window.hMoonSetReorderMode(false);
    }

    window.hMoonSetReorderMode = function (on) {
        var container = document.getElementById('h-moons-container');
        if (!container) return false;
        var next = (on === undefined) ? !container.classList.contains('h-reorder-mode') : !!on;
        container.classList.toggle('h-reorder-mode', next);
        var btn = document.getElementById('hMoonShuffleBtn');
        if (btn) {
            btn.classList.toggle('active', next);
            btn.setAttribute('aria-pressed', next ? 'true' : 'false');
        }
        // Capture phase, so a tap on a control that stops propagation still
        // dismisses (mirrors the toolbar FAB's own outside handler).
        if (next) {
            document.addEventListener('pointerdown', hmReorderOutside, true);
            document.addEventListener('keydown', hmReorderKey);
        } else {
            document.removeEventListener('pointerdown', hmReorderOutside, true);
            document.removeEventListener('keydown', hmReorderKey);
        }
        refreshReorderArrows();
        return next;
    };

    function setupReorderControls() {
        var shuffleBtn = document.getElementById('hMoonShuffleBtn');
        if (shuffleBtn) shuffleBtn.setAttribute('aria-pressed', 'false');
        hDelegate(document, 'click', '#hMoonShuffleBtn', function (e) {
            e.preventDefault();
            window.hMoonSetReorderMode();
        });

        hDelegate(document, 'click', '.h-reorder-btn', function (e) {
            e.preventDefault();
            e.stopPropagation();   // card headers toggle the accordion
            if (this.classList.contains('h-reorder-off')) return;
            var id = parseInt(this.getAttribute('data-reorder-moon'), 10);
            var dir = this.classList.contains('h-reorder-prev') ? -1 : 1;
            window.hMoonSwapMoons(id, id + dir);
        });
        refreshReorderArrows();
    }

    // Expose updateTemplateLayout for external use (e.g., order reload)
    window.updateHMoonTemplateLayout = function(count) {
        updateTemplateLayout(count);
    };

})();
